/**
 * Embed Service - Application Layer
 *
 * Handles multi-step workflow for opening embed URLs in popup windows.
 * Centralizes popup logic that was previously in components.
 */

import type { EmbedJoinResponseDto } from "@semo/api-contracts";
import { toast } from "sonner";

import {
  SESSION_EXPIRED_MESSAGE,
  SESSION_EXPIRED_TOAST_ID,
} from "@/utils/sessionExpired";

/** 응답 오류가 401 인가 — axios 오류·ApiError 어느 모양이든 status 만 본다. */
function isUnauthorizedError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  if ("statusCode" in error && error.statusCode === 401) return true;
  if ("status" in error && error.status === 401) return true;
  if ("response" in error) {
    const { response } = error;
    return (
      typeof response === "object" &&
      response !== null &&
      "status" in response &&
      response.status === 401
    );
  }
  // 갱신 토큰이 없어 공용 도우미가 갱신을 포기한 경우(로그인 만료와 같은 뜻)
  return (
    error instanceof Error && error.message === "No refresh token available"
  );
}

export class EmbedService {
  /**
   * Open URL in centered popup window
   */
  static openEmbedPopup(embedUrl: string): void {
    const popupWidth = 1200;
    const popupHeight = 900;

    // Calculate center position relative to current window
    const screenLeft = window.screenLeft || window.screenX || 0;
    const screenTop = window.screenTop || window.screenY || 0;
    const left = screenLeft + (window.outerWidth - popupWidth) / 2;
    const top = screenTop + (window.outerHeight - popupHeight) / 2;

    const features = [
      `width=${popupWidth}`,
      `height=${popupHeight}`,
      `left=${Math.round(left)}`,
      `top=${Math.round(top)}`,
      "resizable=yes",
      "scrollbars=yes",
    ].join(",");

    window.open(embedUrl, "embed_popup", features);
  }

  /**
   * Extract embed URL from API response and open popup
   */
  static handleEmbedUrlResponse(
    response: { data?: EmbedJoinResponseDto } | null | undefined,
  ): void {
    const result = response?.data;

    if (!result?.embedUrl) {
      console.error(
        "[EmbedService] Embed URL not found in response:",
        response,
      );
      toast.error("화면 주소를 받지 못했습니다. 잠시 후 다시 시도해주세요.");
      return;
    }

    const url = String(result.embedUrl);
    console.log("[EmbedService] Opening embed URL:", url);

    // Sanity check for double slashes (excluding protocol)
    if (url.replace("://", "").includes("//")) {
      console.warn("[EmbedService] Double slashes detected in URL:", url);
    }

    this.openEmbedPopup(url);
  }

  /**
   * Handle embed URL error
   */
  static handleEmbedUrlError(error: unknown): void {
    console.error("[EmbedService] Failed to get embed URL:", error);
    // 로그인이 만료됐는데 «견적 참여 페이지를 여는데 실패» 라고만 해서 원인을 알 수 없었다(V-15·A3-15).
    if (isUnauthorizedError(error)) {
      toast.error(SESSION_EXPIRED_MESSAGE, { id: SESSION_EXPIRED_TOAST_ID });
      return;
    }
    toast.error("화면을 열지 못했습니다. 잠시 후 다시 시도해주세요.");
  }
}
